// src/rms/pages/Reports.js
// ============================================================================
// Daily and monthly business reports
// ============================================================================
// Everything here is read from the general ledger, not recomputed from
// invoices -- see reportService for why that distinction is the whole point.
//
// The rows are fetched ON a PIN, not hidden behind one. rms_gl_entries has no
// SELECT grant at all: the only way in is rms_ledger_entries, which checks the
// PIN, checks the role behind it, records that somebody read the accounts, and
// then returns rows. So there is nothing in the page for a modal to be placed
// in front of, and devtools has nothing to read through.
//
// The route is also capability-gated to money rights, but that is courtesy --
// the database is what refuses.
//
// No ad surfaces. A screen showing a business its own revenue is not a place
// to sell an impression.
// ============================================================================

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useRms } from "../context/RmsContext";
import reportService, { ACCOUNT_LABELS } from "../data/reportService";
import { PageHeader, Stat, Banner, EmptyState } from "../components/ui";
import PinModal from "../components/PinModal";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

/** yyyy-mm-dd in UTC, matching how entry_date is stored. */
const isoDate = (d) =>
  [d.getUTCFullYear(), String(d.getUTCMonth() + 1).padStart(2, "0"), String(d.getUTCDate()).padStart(2, "0")].join("-");

function Reports() {
  const { operator, t, formatMoney } = useRms();

  const [tab, setTab] = useState("day");
  const [day, setDay] = useState(() => isoDate(new Date()));
  const [month, setMonth] = useState(() => {
    const n = new Date();
    return `${n.getUTCFullYear()}-${String(n.getUTCMonth() + 1).padStart(2, "0")}`;
  });

  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  /**
   * The PIN, held for as long as this screen is open and no longer.
   *
   * Not persisted anywhere: walk away from the terminal, come back, and the
   * accounts are closed again. That is the point of elevating per screen
   * rather than holding a session open, and it is why the value lives in
   * component state rather than in context or storage.
   *
   * It is kept at all only so that changing the date or the tab does not
   * demand the PIN again -- a report nobody can page through is a report
   * nobody reads.
   */
  const [pin, setPin] = useState(null);

  const load = useCallback(async (withPin) => {
    if (!withPin) return;
    setBusy(true);
    setError(null);
    const result =
      tab === "day"
        ? await reportService.dailyReport(operator, day, withPin)
        : await reportService.monthlyReport(
            operator,
            Number(month.slice(0, 4)),
            Number(month.slice(5, 7)),
            withPin
          );
    if (result.success) setReport(result.data);
    else setError(result.error);
    setBusy(false);
    return result;
  }, [operator, tab, day, month]);

  // Re-reads when the period changes, but only once a PIN has been accepted.
  useEffect(() => {
    if (pin) load(pin);
  }, [load, pin]);

  /**
   * Escape shuts the accounts again.
   *
   * For an owner who has looked at the figures and wants the screen locked
   * before walking away from the counter. The REPORT is cleared as well as the
   * PIN, and that is the part that matters: clearing only the PIN would put the
   * prompt back over figures still sitting in the page, which is the curtain
   * this screen was built to avoid.
   */
  useEffect(() => {
    if (!pin) return undefined;
    const onKey = (e) => {
      if (e.key !== "Escape" || e.isComposing) return;
      setPin(null);
      setReport(null);
      setError(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [pin]);

  /**
   * Unlocking IS the first fetch.
   *
   * The rows do not exist in the page until this succeeds -- rms_gl_entries
   * has no SELECT grant, so there is nothing for a modal to be placed in front
   * of. A wrong PIN returns the database's refusal to the modal, which is what
   * keeps the lockout meaningful.
   */
  const unlock = async (entered) => {
    const result = await load(entered);
    if (!result?.success) return { error: result?.error || "That PIN was not recognised." };
    setPin(entered);
    return {};
  };

  /**
   * The busiest day of the month, for the bar chart's scale.
   *
   * Taken across both revenue and cash so the two series stay comparable to
   * each other -- scaling each to its own maximum would draw a quiet day's
   * cash as tall as a busy day's revenue.
   */
  const peak = useMemo(() => {
    if (!report?.days) return 0;
    return report.days.reduce(
      (m, d) => Math.max(m, Math.abs(d.net_revenue_cents), Math.abs(d.cash_in_cents)),
      0
    );
  }, [report]);

  const empty = report && report.entry_count === 0;

  return (
    <>
      <PageHeader
        title={t("reports.title", "Reports")}
        subtitle={t("reports.subtitle", "Read from the ledger, so the same period always reports the same figures.")}
      />

      <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
        {["day", "month"].map((key) => (
          <button
            key={key}
            type="button"
            className={`rms-btn ${tab === key ? "" : "rms-btn--ghost"} rms-btn--sm`}
            onClick={() => setTab(key)}
          >
            {key === "day" ? t("reports.daily", "Daily") : t("reports.monthly", "Monthly")}
          </button>
        ))}

        <span style={{ flex: 1 }} />

        {tab === "day" ? (
          <input
            type="date"
            className="rms-input"
            style={{ maxWidth: 190 }}
            value={day}
            onChange={(e) => setDay(e.target.value)}
          />
        ) : (
          <input
            type="month"
            className="rms-input"
            style={{ maxWidth: 190 }}
            value={month}
            onChange={(e) => setMonth(e.target.value)}
          />
        )}
      </div>

      {!pin ? (
        <PinModal
          title={t("reports.unlock", "Open the accounts")}
          explain={t("reports.unlockHelp", "Your PIN opens the reports and records that you looked.")}
          onCancel={() => window.history.back()}
          onResolved={unlock}
        />
      ) : null}

      {pin ? (
        <div className="rms-pin-unlocked">
          <span>{t("pinGate.unlocked", "Unlocked with your PIN.")}</span>
          <button type="button" className="rms-linkish" onClick={() => { setPin(null); setReport(null); }}>
            {t("pinGate.lockNow", "Lock this screen (Esc)")}
          </button>
        </div>
      ) : null}

      {error ? <Banner tone="danger">{error}</Banner> : null}

      {busy ? (
        <p style={{ fontSize: 13.5, color: "var(--rms-muted-strong)" }}>{t("common.loading", "Loading…")}</p>
      ) : empty ? (
        <EmptyState
          title={t("reports.noneTitle", "Nothing posted for this period")}
          body={t(
            "reports.noneBody",
            "Invoices, payments and deposits post to the ledger as they happen. A period with no activity reports nothing rather than zero."
          )}
        />
      ) : report ? (
        <>
          <div className="rms-stats" style={{ marginBottom: 18 }}>
            <Stat
              label={t("reports.netRevenue", "Net revenue")}
              value={formatMoney(report.net_revenue_cents)}
              hint={
                report.refunds_cents
                  ? t("reports.afterRefunds", "after {amount} refunded", {
                      amount: formatMoney(report.refunds_cents),
                    })
                  : t("reports.earned", "earned, not collected")
              }
            />
            <Stat
              label={t("reports.cashIn", "Cash in")}
              value={formatMoney(report.cash_in_cents)}
              hint={t("reports.cashHint", "money that actually moved")}
            />
            <Stat
              label={t("reports.taxCollected", "Tax collected")}
              value={formatMoney(report.tax_collected_cents)}
              hint={t("reports.taxHint", "owed to an authority, not earnings")}
            />
            <Stat
              label={t("reports.deposits", "Deposits")}
              value={formatMoney(report.deposits_movement_cents)}
              hint={t("reports.depositsHint", "customers' money held")}
            />
          </div>

          {/* Revenue by account. The ledger's own breakdown -- rental, add-ons,
              damage, forfeited deposits -- rather than a category invented
              here, so it reconciles against the accounts by construction. */}
          <div className="rms-card rms-card-pad" style={{ marginBottom: 18 }}>
            <h3 className="rms-section-title">{t("reports.revenueBreakdown", "Where the revenue came from")}</h3>
            {report.revenue.length ? (
              <table className="rms-table">
                <tbody>
                  {report.revenue.map((r) => (
                    <tr key={r.account}>
                      <td>{ACCOUNT_LABELS[r.account] || r.account}</td>
                      <td className="rms-num">{formatMoney(r.cents)}</td>
                    </tr>
                  ))}
                  {report.refunds_cents ? (
                    <tr>
                      <td>{ACCOUNT_LABELS["4900"]}</td>
                      <td className="rms-num">−{formatMoney(report.refunds_cents)}</td>
                    </tr>
                  ) : null}
                  <tr>
                    <td><strong>{t("reports.netRevenue", "Net revenue")}</strong></td>
                    <td className="rms-num"><strong>{formatMoney(report.net_revenue_cents)}</strong></td>
                  </tr>
                </tbody>
              </table>
            ) : (
              <p style={{ fontSize: 13, color: "var(--rms-muted-strong)" }}>
                {t("reports.noRevenue", "No revenue was recognised in this period.")}
              </p>
            )}
          </div>

          {/* Per rate, because this is the figure a return is filed from. One
              blended tax total cannot be remitted to anybody. */}
          {report.tax_by_rate.length ? (
            <div className="rms-card rms-card-pad" style={{ marginBottom: 18 }}>
              <h3 className="rms-section-title">{t("reports.taxToRemit", "Tax to remit")}</h3>
              <table className="rms-table">
                <tbody>
                  {report.tax_by_rate.map((tx) => (
                    <tr key={tx.tax_rate_id || tx.name}>
                      <td>{tx.name}</td>
                      <td className="rms-num">{formatMoney(tx.cents)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}

          {report.days ? (
            <div className="rms-card rms-card-pad">
              <h3 className="rms-section-title">{t("reports.byDay", "Day by day")}</h3>
              <div style={{ display: "flex", alignItems: "flex-end", gap: 3, height: 120, marginTop: 12 }}>
                {report.days.map((d) => (
                  <div
                    key={d.date}
                    title={`${d.date} · ${formatMoney(d.net_revenue_cents)}`}
                    style={{
                      flex: 1,
                      minWidth: 4,
                      height: peak ? `${Math.max(2, (Math.abs(d.net_revenue_cents) / peak) * 100)}%` : 2,
                      background: d.net_revenue_cents < 0 ? "var(--rms-danger)" : "var(--rms-accent)",
                      borderRadius: 2,
                    }}
                  />
                ))}
              </div>
              <p style={{ fontSize: 12, color: "var(--rms-muted-strong)", marginTop: 8 }}>
                {t("reports.byDayHint", "Net revenue per day. Hover a bar for the figure.")}
              </p>
            </div>
          ) : null}
        </>
      ) : null}
    </>
  );
}

export default Reports;
